// Visitas de las páginas públicas (portada, Chatbularis, consulta de estado):
// cuánta gente llega, de dónde, y cuántos pasan a probar. Para medir la
// publicidad. Los datos vienen de GET /plataforma/visitas (ver backend/src/lib/visitas.ts).
import React, { useEffect, useMemo, useState } from 'react';

const C = { navy: '#0D1B35', blue: '#1A3A6E', line: '#DDE6F3', muted: '#6B82A0', grid: '#EEF2F8' };
// Validadas con la guía de gráficos (daltonismo y separación). El naranjo queda
// bajo 3:1 contra el blanco: por eso la tabla y las etiquetas directas.
const SERIE = { visitas: '#2756A8', nuevos: '#D9822B' };

type Fila = { dia: string; pagina: string; fuente: string; evento: string; total: number; nuevos: number };
type Datos = { hoy: string; dias: number; filas: Fila[]; registros: { dia: string; n: number }[] };

const PAGINAS: Record<string, string> = { portada: 'Portada', chatbularis: 'Chatbularis', aprende: 'Aprende', estado: 'Consulta de estado' };
const RANGOS: [number, string][] = [[7, '7 días'], [30, '30 días'], [90, '90 días']];

function diasHasta(hoy: string, n: number): string[] {
  const [a, m, d] = hoy.split('-').map(Number);
  return Array.from({ length: n }, (_, i) => { const f = new Date(Date.UTC(a, m - 1, d - (n - 1 - i))); return f.toISOString().slice(0, 10); });
}
const fmtDia = (s: string) => { const [, m, d] = s.split('-'); return `${Number(d)}/${Number(m)}`; };
const mil = (n: number) => n.toLocaleString('es-CL');

const Tile: React.FC<{ etiqueta: string; valor: number; nota?: string }> = ({ etiqueta, valor, nota }) => (
  <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '14px 16px', minWidth: 0 }}>
    <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{etiqueta}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: C.navy, fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>{mil(valor)}</div>
    {nota && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{nota}</div>}
  </div>
);

/** Visitas y personas nuevas por día: dos líneas en una misma escala. */
const GraficoDias: React.FC<{ dias: string[]; visitas: number[]; nuevos: number[] }> = ({ dias, visitas, nuevos }) => {
  const [hover, setHover] = useState<number | null>(null);
  const W = 900, H = 260, L = 44, R = 16, T = 16, B = 30;
  const max = Math.max(4, ...visitas);
  const paso = Math.pow(10, Math.floor(Math.log10(max)));
  const tope = Math.ceil(max / paso) * paso;
  const x = (i: number) => L + (dias.length === 1 ? 0 : (i * (W - L - R)) / (dias.length - 1));
  const y = (v: number) => T + (H - T - B) * (1 - v / tope);
  const linea = (vs: number[]) => vs.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const marcas = [0, tope / 2, tope];
  const cadaN = Math.ceil(dias.length / 8);
  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
        aria-label="Visitas y personas nuevas por día"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - r.left) / r.width) * W;
          const i = Math.round(((px - L) / (W - L - R)) * (dias.length - 1));
          setHover(Math.max(0, Math.min(dias.length - 1, i)));
        }}>
        {marcas.map((m) => (
          <g key={m}>
            <line x1={L} x2={W - R} y1={y(m)} y2={y(m)} stroke={C.grid} strokeWidth={1} />
            <text x={L - 8} y={y(m) + 4} textAnchor="end" fontSize={11} fill={C.muted}>{mil(m)}</text>
          </g>
        ))}
        {dias.map((d, i) => ((i % cadaN === 0 && dias.length - 1 - i >= cadaN / 2) || i === dias.length - 1) && (
          <text key={d} x={x(i)} y={H - 8} textAnchor="middle" fontSize={11} fill={C.muted}>{fmtDia(d)}</text>
        ))}
        <path d={`${linea(visitas)} L${x(dias.length - 1)},${y(0)} L${x(0)},${y(0)} Z`} fill={SERIE.visitas} opacity={0.08} />
        <path d={linea(visitas)} fill="none" stroke={SERIE.visitas} strokeWidth={2} strokeLinejoin="round" />
        <path d={linea(nuevos)} fill="none" stroke={SERIE.nuevos} strokeWidth={2} strokeLinejoin="round" />
        {hover !== null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} stroke={C.muted} strokeWidth={1} strokeDasharray="3 3" />
            <circle cx={x(hover)} cy={y(visitas[hover])} r={4.5} fill={SERIE.visitas} stroke="#fff" strokeWidth={2} />
            <circle cx={x(hover)} cy={y(nuevos[hover])} r={4.5} fill={SERIE.nuevos} stroke="#fff" strokeWidth={2} />
          </g>
        )}
        {dias.length > 0 && (
          <>
            <text x={x(dias.length - 1) - 4} y={y(visitas[dias.length - 1]) - 8} textAnchor="end" fontSize={12} fontWeight={600} fill={C.navy}>{mil(visitas[dias.length - 1])} visitas</text>
          </>
        )}
      </svg>
      {hover !== null && (
        <div style={{ position: 'absolute', top: 8, left: `${(x(hover) / W) * 100}%`, transform: `translateX(${hover > dias.length / 2 ? '-105%' : '5%'})`, background: '#fff', border: `1px solid ${C.line}`, borderRadius: 8, padding: '8px 10px', fontSize: 12, boxShadow: '0 4px 14px rgba(13,27,53,0.12)', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
          <div style={{ fontWeight: 600, color: C.navy, marginBottom: 4 }}>{fmtDia(dias[hover])}</div>
          <div style={{ color: C.navy }}><span style={{ display: 'inline-block', width: 10, height: 2, background: SERIE.visitas, marginRight: 6, verticalAlign: 'middle' }} />Visitas: <b>{mil(visitas[hover])}</b></div>
          <div style={{ color: C.navy }}><span style={{ display: 'inline-block', width: 10, height: 2, background: SERIE.nuevos, marginRight: 6, verticalAlign: 'middle' }} />Personas nuevas: <b>{mil(nuevos[hover])}</b></div>
        </div>
      )}
    </div>
  );
};

const Visitas: React.FC<{ api: <T>(ruta: string, opciones?: any) => Promise<T> }> = ({ api }) => {
  const [dias, setDias] = useState(30);
  const [datos, setDatos] = useState<Datos | null>(null);
  const [error, setError] = useState('');
  const [verTabla, setVerTabla] = useState(false);

  useEffect(() => {
    setError('');
    api<Datos>(`/plataforma/visitas?dias=${dias}`).then(setDatos).catch((e) => setError(e?.message || 'No se pudieron cargar las visitas.'));
  }, [dias, api]);

  const r = useMemo(() => {
    if (!datos) return null;
    const lista = diasHasta(datos.hoy, datos.dias);
    const visitas = datos.filas.filter((f) => f.evento === 'visita');
    const suma = (fs: Fila[], k: 'total' | 'nuevos') => fs.reduce((s, f) => s + f[k], 0);
    const porDia = (k: 'total' | 'nuevos') => lista.map((d) => suma(visitas.filter((f) => f.dia === d), k));
    const semana = new Set(lista.slice(-7));
    const fuentes = new Map<string, { total: number; nuevos: number }>();
    visitas.forEach((f) => { const a = fuentes.get(f.fuente) || { total: 0, nuevos: 0 }; a.total += f.total; a.nuevos += f.nuevos; fuentes.set(f.fuente, a); });
    const paginas = new Map<string, number>();
    visitas.forEach((f) => paginas.set(f.pagina, (paginas.get(f.pagina) || 0) + f.total));
    return {
      lista, visitasDia: porDia('total'), nuevosDia: porDia('nuevos'),
      hoy: suma(visitas.filter((f) => f.dia === datos.hoy), 'total'),
      semana: suma(visitas.filter((f) => semana.has(f.dia)), 'total'),
      periodo: suma(visitas, 'total'), nuevos: suma(visitas, 'nuevos'),
      clics: suma(datos.filas.filter((f) => f.evento === 'clic_prueba'), 'total'),
      videos: suma(datos.filas.filter((f) => f.evento === 'video_carrusel'), 'total'),
      registros: datos.registros.reduce((s, x) => s + x.n, 0),
      fuentes: [...fuentes.entries()].sort((a, b) => b[1].total - a[1].total),
      paginas: [...paginas.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [datos]);

  const maxFuente = r ? Math.max(1, ...r.fuentes.map(([, v]) => v.total)) : 1;
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0, color: C.navy }}>Visitas</h1>
          <p style={{ color: C.muted, margin: '4px 0 0', fontSize: 14, maxWidth: 640 }}>
            Gente que llega a tabularis.cl, a la página de Chatbularis y a la consulta de estado. No guarda nada de la persona: solo cuenta.
            Para medir una campaña, usa enlaces con <code>?utm_source=instagram</code> (o el nombre que quieras).
          </p>
        </div>
        <div role="group" aria-label="Período" style={{ display: 'flex', gap: 6 }}>
          {RANGOS.map(([n, et]) => (
            <button key={n} onClick={() => setDias(n)} aria-pressed={dias === n}
              style={{ border: `1px solid ${dias === n ? C.navy : C.line}`, background: dias === n ? C.navy : '#fff', color: dias === n ? '#fff' : C.navy, borderRadius: 999, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>{et}</button>
          ))}
        </div>
      </div>
      {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 14 }}>{error}</div>}
      {!r ? <p style={{ color: C.muted, marginTop: 18 }}>Cargando…</p> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginTop: 18 }}>
            <Tile etiqueta="Hoy" valor={r.hoy} nota="visitas" />
            <Tile etiqueta="Últimos 7 días" valor={r.semana} nota="visitas" />
            <Tile etiqueta={`Últimos ${dias} días`} valor={r.periodo} nota={`${mil(r.nuevos)} personas nuevas`} />
            <Tile etiqueta="Clics en «Probar gratis»" valor={r.clics} nota={`en ${dias} días`} />
            <Tile etiqueta="Estudios registrados" valor={r.registros} nota={`en ${dias} días`} />
          </div>

          <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: 16, marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div style={{ fontWeight: 600, color: C.navy }}>Visitas por día</div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', fontSize: 12, color: C.navy }}>
                <span><span style={{ display: 'inline-block', width: 14, height: 2, background: SERIE.visitas, marginRight: 6, verticalAlign: 'middle' }} />Visitas</span>
                <span><span style={{ display: 'inline-block', width: 14, height: 2, background: SERIE.nuevos, marginRight: 6, verticalAlign: 'middle' }} />Personas nuevas</span>
                <button onClick={() => setVerTabla((v) => !v)} style={{ background: 'none', border: `1px solid ${C.line}`, borderRadius: 6, padding: '3px 9px', fontSize: 12, color: C.muted, cursor: 'pointer' }}>{verTabla ? 'Ver gráfico' : 'Ver tabla'}</button>
              </div>
            </div>
            {verTabla ? (
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                  <thead><tr style={{ color: C.muted, textAlign: 'left' }}><th style={{ padding: 6 }}>Día</th><th style={{ padding: 6, textAlign: 'right' }}>Visitas</th><th style={{ padding: 6, textAlign: 'right' }}>Personas nuevas</th></tr></thead>
                  <tbody>{[...r.lista].reverse().map((d, k) => { const i = r.lista.length - 1 - k; return (
                    <tr key={d} style={{ borderTop: `1px solid ${C.line}` }}><td style={{ padding: 6 }}>{fmtDia(d)}</td><td style={{ padding: 6, textAlign: 'right' }}>{mil(r.visitasDia[i])}</td><td style={{ padding: 6, textAlign: 'right' }}>{mil(r.nuevosDia[i])}</td></tr>
                  ); })}</tbody>
                </table>
              </div>
            ) : <GraficoDias dias={r.lista} visitas={r.visitasDia} nuevos={r.nuevosDia} />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginTop: 14 }}>
            <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: 16, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: C.navy, marginBottom: 10 }}>De dónde llegan</div>
              {r.fuentes.length === 0 ? <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>Todavía no hay visitas en este período.</p> :
                r.fuentes.map(([f, v]) => (
                  <div key={f} title={`${mil(v.total)} visitas · ${mil(v.nuevos)} personas nuevas`} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 56px', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 13 }}>
                    <span style={{ color: C.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>{f}</span>
                    <span style={{ background: C.grid, borderRadius: 4, height: 10 }}><span style={{ display: 'block', height: 10, width: `${(v.total / maxFuente) * 100}%`, background: SERIE.visitas, borderRadius: 4 }} /></span>
                    <span style={{ textAlign: 'right', color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{mil(v.total)}</span>
                  </div>
                ))}
            </div>
            <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: 16, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: C.navy, marginBottom: 10 }}>Páginas</div>
              {r.paginas.length === 0 ? <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>Sin datos todavía.</p> :
                r.paginas.map(([p, n]) => (
                  <div key={p} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderTop: `1px solid ${C.line}` }}>
                    <span style={{ color: C.navy }}>{PAGINAS[p] || p}</span><span style={{ fontVariantNumeric: 'tabular-nums', color: C.navy }}>{mil(n)}</span>
                  </div>
                ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderTop: `1px solid ${C.line}`, color: C.muted }}>
                <span>Videos vistos en el carrusel</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{mil(r.videos)}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Visitas;
